<template>
  <div class="p-6 dark:text-gray-50" v-if="canAddHostAvailability || user?.is_superuser">
    <div class="flex space-x-3 mb-6 items-center">
      <h1 class="text-2xl font-semibold dark:text-white">Add New Host Availability{{ isMultiSubmit ? ' (Optional)' : '' }}</h1>
      <div class="relative group">
        <button
          type="button"
          class="bg-transparent hover:bg-transparent"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
        <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-2 py-0.5 glass-card opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
      </div>
    </div>

    <div
      v-if="error"
      class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
      role="alert"
    >
      <strong class="font-bold">Error!</strong>
      <span class="block sm:inline">{{ error }}</span>
    </div>

    <form @submit.prevent="submitForm" class="space-y-6 glass-card p-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div v-for="field in visibleFormFields" :key="field.key" class="flex flex-col">
        <div class="mb-1 flex items-center gap-1.5">
          <label :for="field.key" class="font-medium dark:text-white">
            {{ field.label }}<span v-if="field.required" class="pl-1 text-red-500">*</span>
          </label>
          <FieldHelp v-if="field.help" :text="field.help" />
        </div>

        <GlassSelect
          v-if="field.type === 'select'"
          :id="field.key"
          v-model="formData[field.key]"
          :options="field.options"
          :error="!!fieldErrors[field.key]"
          @focus="clearFieldError(field.key)"
          @change="clearFieldError(field.key)"
        />
        <input
          v-else-if="field.type === 'checkbox'"
          :id="field.key"
          type="checkbox"
          v-model="formData[field.key]"
          class="h-5 w-5 mt-1 self-start"
        />
        <input
          v-else
          :id="field.key"
          v-model="formData[field.key]"
          :type="field.type"
          :placeholder="field.placeholder"
          @focus="clearFieldError(field.key)"
          :class="[
            fieldErrors[field.key]
              ? 'rounded px-3 py-2 ring-2 ring-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 mb-2'
              : 'rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2',
          ]"
        />
        <FieldError :message="fieldErrors[field.key]" />
      </div>
      </div>

      <div class="flex justify-end space-x-2">
        <button v-if="!isMultiSubmit" type="button" @click="goBack" class="btn-cancel">Cancel</button>
        <button v-else type="button" @click="skipMultiSubmit" class="btn bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200">Skip</button>
        <button
          id="submit-host-availability-btn"
          type="submit"
          :disabled="tutorialActive"
          :class="tutorialActive ? 'opacity-50 cursor-not-allowed' : ''"
          class="btn bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Add Host Availability
        </button>
      </div>
    </form>
  </div>

  <PageError
    v-if="permsLoaded && !canAddHostAvailability && !user?.is_superuser"
    :error="errorMessages['403']"
    :errorCode="'403'"
  />
</template>

<script>
import { createHostAvailability } from "@/services/hostAvailabilityService";
import GlassSelect from "@/components/GlassSelect.vue";
import FieldError from "@/components/FieldError.vue";
import FieldHelp from "@/components/FieldHelp.vue";
import { useStore } from "@/store";
import PageError from "@/components/PageError.vue";
import { errorMessages, extractErrorMessage } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";
import { useTutorial } from "@/composables/tutorial";

export default {
  name: "AddHostAvailability",
  components: {
    FieldError,
    FieldHelp,
    GlassSelect,
    PageError,
  },
  emits: ["host-availability-submitted", "host-availability-skipped"],
  props: {
    isMultiSubmit: { type: Boolean, default: false },
    submittedSite: { type: Object, default: null },
  },
  setup() {
    const tenant = useMultiTenant();
    const { start } = useTutorial();
    return { tenant, _start: start };
  },
  data() {
    return {
      error: "",
      tutorialActive: false,
      errorMessages,
      fieldErrors: {},
      formData: {
        host: "",
        os: "",
        port: "",
        module: "",
        application: "",
        ip_addr: "",
        domain: "",
        scrape_interval: 60,
        scrape_timeout: 30,
        target: "",
        technical_contact: "",
        in_maintenance: false,
      },
      formFields: [
        { key: "host", label: "Host", type: "text", required: true, placeholder: "e.g. web-server-01", help: "Hostname of the machine being monitored." },
        { key: "os", label: "Operating System", type: "select", required: true, options: ["Linux", "Windows"], help: "Operating system running on the host." },
        { key: "port", label: "Port", type: "text", required: true, placeholder: "e.g. 443", help: "Network port the availability check connects to (1-65535)." },
        { key: "module", label: "Module", type: "select", required: true, options: ["http_2xx", "icmp", "udp", "tcp_connect"], help: "Probe type used to check the target: HTTP, ICMP, UDP, or TCP." },
        { key: "application", label: "Application", type: "text", required: true, placeholder: "e.g. Customer Portal", help: "Name of the application running on this host." },
        { key: "ip_addr", label: "IP Address", type: "text", required: true, placeholder: "e.g. 192.168.1.10", help: "IP address of the host." },
        { key: "domain", label: "Domain", type: "text", required: true, placeholder: "e.g. STAGING", help: "Environment this host belongs to, e.g. dev, staging, or prod." },
        { key: "target", label: "Target", type: "text", required: true, placeholder: "e.g. https://example.com", help: "URL or address the check hits." },
        { key: "scrape_interval", label: "Scrape Interval (seconds)", type: "number", required: true, staffOnly: true, placeholder: "60", help: "How often, in seconds, the check runs." },
        { key: "scrape_timeout", label: "Scrape Timeout (seconds)", type: "number", required: true, staffOnly: true, placeholder: "30", help: "How long, in seconds, to wait before the check times out. Must not exceed the interval." },
        { key: "technical_contact", label: "Technical Contact", type: "text", required: true, placeholder: "e.g. Jane Doe", help: "Person responsible for this host." },
        { key: "in_maintenance", label: "In Maintenance", type: "checkbox", help: "Pause alerting while the host is under maintenance." },
      ],
      addHostAvailabilityTutorialSteps: [
        {
          element: '#host',
          action: 'type',
          value: 'web-server-01',
          message: 'Enter the hostname of the machine to monitor.'
        },
        {
          element: '#os',
          action: 'select',
          value: 'Linux',
          message: 'Select the operating system running on the host: Linux or Windows.'
        },
        {
          element: '#port',
          action: 'type',
          value: '443',
          message: 'Enter the port the availability check should connect to.'
        },
        {
          element: '#module',
          action: 'select',
          value: 'http_2xx',
          message: 'Select the check module: HTTP, ICMP, UDP, or TCP.'
        },
        {
          element: '#application',
          action: 'type',
          value: 'Customer Portal',
          message: 'Enter the application running on this host.'
        },
        {
          element: '#ip_addr',
          action: 'type',
          value: '192.168.1.10',
          message: 'Enter the IP address of the host.'
        },
        {
          element: '#domain',
          action: 'type',
          value: 'prod',
          message: 'The environment this host belongs to, e.g. dev, staging, or prod.'
        },
        {
          element: '#target',
          action: 'type',
          value: 'https://example.com',
          message: 'Enter the target URL or address the check will hit.'
        },
        {
          element: '#technical_contact',
          action: 'type',
          value: 'Jane Doe',
          message: 'Enter the name of the technical contact for this host.'
        },
        {
          element: '#in_maintenance',
          action: 'display',
          message: 'Tick this to mark the host as in maintenance and pause alerting.'
        },
        {
          element: '#submit-host-availability-btn',
          action: 'display',
          message: 'Click to create the host availability check.'
        },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    canAddHostAvailability() {
      return useStore.hasPermission("Can add Host Availability");
    },
    visibleFormFields() {
      return this.formFields.filter((field) => !field.staffOnly || useStore.isStaff());
    },
  },
  methods: {
    async startTutorial() {
      this.tutorialActive = true;
      const originalFormData = { ...this.formData };
      await this._start(this.addHostAvailabilityTutorialSteps, () => {
        this.tutorialActive = false;
        this.formData = { ...originalFormData };
      });
    },
    clearFieldError(fieldName) {
      this.error = "";
      if (this.fieldErrors[fieldName]) {
        delete this.fieldErrors[fieldName];
      }
    },
    goBack() {
      this.$router.push({
        name: "HostAvailabilities",
        query: this.tenant.addSchemaParam(),
      });
    },
    skipMultiSubmit() {
      this.$emit("host-availability-skipped", "skipped");
    },
    validateForm() {
      this.fieldErrors = {};
      this.visibleFormFields.forEach((field) => {
        const value = this.formData[field.key];
        if (field.required && (value === "" || value === null || value === undefined)) {
          this.fieldErrors[field.key] = "This field is required.";
        }
      });
      if (this.formData.port !== "" && this.formData.port !== null && !this.fieldErrors.port) {
        const port = Number(this.formData.port);
        if (!Number.isInteger(port) || port < 1 || port > 65535) {
          this.fieldErrors.port = "Please enter a valid port number.";
        }
      }
      const interval = Number(this.formData.scrape_interval);
      const timeout = Number(this.formData.scrape_timeout);
      if (interval && timeout && timeout > interval) {
        this.fieldErrors.scrape_timeout = "Scrape timeout cannot exceed scrape interval.";
      }
      return Object.keys(this.fieldErrors).length === 0;
    },
    async submitForm() {
      if (this.tutorialActive) return;
      this.error = "";
      try {
        const validation = this.tenant.validateSchemaForOperation();
        if (!validation.valid) {
          this.error = "Malformed request. Failed to create Host Availability!";
          return;
        }
        if (!this.validateForm()) return;
        const payload = { ...this.formData };
        payload.port = Number(this.formData.port);
        if (!useStore.isStaff()) {
          payload.scrape_interval = 60;
          payload.scrape_timeout = 30;
        }
        const created = await createHostAvailability(payload, validation.client);
        if (this.isMultiSubmit) this.$emit("host-availability-submitted", created);
        else this.goBack();
      } catch (error) {
        this.fieldErrors = {};
        const response = error?.response;
        const errData = response?.data;
        const isValidationError =
          response?.status === 400 &&
          errData && typeof errData === "object" && !Array.isArray(errData);
        if (isValidationError) {
          const visibleFieldKeys = new Set(this.visibleFormFields.map((field) => field.key));
          const generalMessages = [];
          Object.entries(errData).forEach(([key, value]) => {
            const raw = Array.isArray(value) ? value[0] : value;
            if (!raw) return;
            const message = typeof raw === "string" ? raw : JSON.stringify(raw);
            if (visibleFieldKeys.has(key)) {
              this.fieldErrors[key] = message;
            } else {
              generalMessages.push(message);
            }
          });
          if (generalMessages.length) {
            this.error = generalMessages.join(" ");
          }
        } else if (response) {
          this.error = extractErrorMessage(
            error,
            " Something went wrong while creating the host availability. Please try again."
          );
        } else {
          this.error = "Network error or server unreachable. Please try again.";
        }
        console.error("Error creating host availability:", error);
      }
    },
  },
};
</script>
